import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { FieldValues, UseFormRegister } from 'react-hook-form';
import { Button, Icon, ProgressBarBox, Typography } from '@/components/common';
import Icons from '@/const/icons';
import { useImagePreview } from '@/hooks/useImagePreview';
import useModal from '@/hooks/useModal';
import { CircleAlert, X } from 'lucide-react';
import { useNotification } from '@/hooks';
import clsx from 'clsx';
import { formatErrorMessage } from '@/lib';
import { logApiError } from '@/lib/logApiError';

const ZERO_PERCENT = 0;
const NINETY_PERCENT = 90;
const ONE_HUNDRED_PERCENT = 100;
interface Props {
	file: File | string;
	isOpen: boolean;
	showPrevFile?: boolean;
	prevFile: string | undefined;
	rules?: Record<string, unknown>;
	register: UseFormRegister<FieldValues>;
	setImage: (url: string | null, file: File | null) => void;
	setIsModalOpen: Dispatch<SetStateAction<boolean>>;
}

export const ModalUploadImage: React.FC<Props> = ({
	file,
	isOpen,
	prevFile,
	rules,
	register,
	setImage,
	showPrevFile = true,
	setIsModalOpen,
}) => {
	const { toastSuccess, toastError } = useNotification();
	const [progress, setProgress] = useState(0);
	const [error, setError] = useState<string>();
	const refInputFile = useRef<HTMLInputElement>(null);
	const { loadImage } = useImagePreview();
	const { Modal, show, hide } = useModal();

	const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];

	useEffect(() => {
		if (isOpen) {
			show();
			setError(undefined); // reset errors
		} else {
			setProgress(0);
			hide();
		}
	}, [isOpen]);

	const onClickUpload = (): void => {
		refInputFile?.current?.click();
	};

	const simulateProgress = async (
		start: number,
		max: number
	): Promise<boolean> => {
		return new Promise((resolve) => {
			let progress = start;
			const interval = setInterval(() => {
				progress += 10;
				setProgress(progress);
				if (progress >= max) {
					clearInterval(interval);
					resolve(true);
				}
			}, 300);
		});
	};

	const isSizeImageValid = (file: File): boolean => {
		if (file.size > 25 * 1000 * 1024) {
			setProgress(0);
			setError(
				'The file you tried to upload is too heavy. Please try again with a file up to 25 MB.'
			);
			return false;
		}

		return true;
	};

	const isTypeImageValid = (file: File): boolean => {
		if (!validImageTypes.includes(file.type)) {
			setProgress(0);
			setError(
				'Select an image in the correct format such as png, jpg or jpeg'
			);
			return false;
		}
		return true;
	};

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const file = e.target.files?.[0];
		setError('');

		if (!file) {
			return;
		}

		if (!isTypeImageValid(file)) {
			console.log('Invalid file type');
			logApiError({
				status: 400,
				message:
					'Error Unsupported Format: Invalid image format. Please upload the image in JPG, JPEG or PNG format.',
				config: { data: '', url: '/clientapp/settings?tab=personal' },
			});
			return;
		}

		if (!isSizeImageValid(file)) {
			console.log('Invalid file size');
			logApiError({
				status: 400,
				message: 'The file you tried to upload is too heavy.',
				config: { data: '', url: '/clientapp/settings?tab=personal' },
			});
			return;
		}

		try {
			simulateProgress(ZERO_PERCENT, NINETY_PERCENT).then(() => {
				loadImage(file)?.then((data) => {
					setImage(data.imageSrc, file);
					setProgress(ONE_HUNDRED_PERCENT);
					setIsModalOpen(false);
					toastSuccess(
						'Your photo has been uploaded successfully. Please remember to save the changes.'
					);
				});
			});
		} catch (error) {
			toastError(formatErrorMessage(undefined));
		} finally {
			if (e.target) {
				e.target.value = '';
			}
		}
	};

	const handleDropFile = (e: React.DragEvent): void => {
		e.preventDefault();
		const file = e.dataTransfer.files[0];

		if (!file) {
			return;
		}

		if (!isTypeImageValid(file)) {
			console.log('Invalid file type');
			return;
		}

		if (!isSizeImageValid(file)) {
			console.log('Invalid file size');
			return;
		}

		try {
			simulateProgress(ZERO_PERCENT, NINETY_PERCENT).then(() => {
				loadImage(file)?.then((data) => {
					setImage(data.imageSrc, file);
					setProgress(ONE_HUNDRED_PERCENT);
					setIsModalOpen(false);
					toastSuccess(
						'Your photo has been uploaded successfully. Please remember to save the changes.'
					);
				});
			});
		} catch (error) {
			toastError(formatErrorMessage(undefined));
		}
	};

	const handleRemoveFile = (): void => {
		setProgress(0);
		setImage(null, null);
	};

	const registerAux = register('file', rules);

	const getFileName = (): string => {
		if (file && typeof file !== 'string' && 'name' in file) return file.name;

		if (prevFile || typeof file === 'string') return 'image';

		return '';
	};

	const preloadFile = typeof file === 'string';
	const isProgress = progress > 0 && progress < 100;

	return (
		<Modal classNameCardContainer="!bg-gradient-to-r from-white to-white md:!min-h-[288px]">
			<div className="w-full md:w-[532px] flex flex-col items-center space-y-6">
				<div id="header" className="space-y-3 w-full relative">
					<div className="flex items-center justify-center">
						<Icon src={Icons.imageCards} className="!w-16" />
						<Typography type="semibold-20">Upload image</Typography>
						<button
							className="absolute right-0 top-2"
							onClick={() => setIsModalOpen(false)}
						>
							<X />
						</button>
					</div>
				</div>
				<div
					id="content"
					className={clsx(
						'md:px-10 border-1 border-opacity-80 rounded-[32px] border border-dashed bg-gray-20',
						{
							'border-opacity-primary': !error,
							'border-red-600': error,
						}
					)}
					onDrop={handleDropFile}
					onDragOver={(e) => e.preventDefault()}
				>
					<div className="py-6 px-8 flex flex-col items-center gap-8">
						<div>
							<Typography type="regular-14" className="opacity-60 text-center">
								Drag and drop your file here, or click to
							</Typography>
							<Typography type="regular-14" className="opacity-60 text-center">
								browse from your device
							</Typography>
						</div>
						{(progress > 0 || (showPrevFile && (file || prevFile))) && (
							<div className="w-48">
								<ProgressBarBox
									percent={progress}
									handleRemove={handleRemoveFile}
									fileName={getFileName()}
									isShowPrevFile={Boolean(prevFile)}
								/>
							</div>
						)}
						{(!isProgress || preloadFile) && (
							<Button
								size="small"
								decoration="primary"
								className="flex items-center gap-2 !h-[30px] !py-4"
								onClick={onClickUpload}
							>
								Upload
								<Icon src={Icons.documentUpload} className="!w-4 !h-4" />
							</Button>
						)}

						{error && (
							<div className="flex items-center gap-1">
								<CircleAlert className="text-red-600 !h-5 !w-5" />
								<div>
									<Typography
										type="regular-12"
										text={error}
										className="text-red-600 max-w-[250px]"
									/>
								</div>
							</div>
						)}
						<input
							{...registerAux.ref}
							ref={refInputFile}
							type="file"
							accept={validImageTypes.join(',')}
							className="hidden"
							onChange={handleImageChange}
						/>
					</div>
				</div>
			</div>
		</Modal>
	);
};
